import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { SYSTEM_DEFAULTS, FONT_OPTIONS } from '../config/tenantDefaults';
import { STANDARD_DEPTS } from '../config/constants';

export function applyBranding(branding = {}) {
  const root  = document.documentElement;
  const font  = FONT_OPTIONS.find(f => f.value === (branding.displayFont || ''));
  root.style.setProperty('--brand-primary',      branding.primaryColor || '#0a0a0a');
  root.style.setProperty('--brand-primary-fg',   branding.primaryFg    || '#ffffff');
  root.style.setProperty('--brand-accent',       branding.primaryColor || '#0a0a0a');
  root.style.setProperty('--brand-display-font', font?.stack ?? 'Georgia, serif');
}

function buildConfig(raw) {
  const merged = { ...SYSTEM_DEFAULTS, ...raw };
  // A tenant that hasn't visited Settings yet has an empty departments/territories list —
  // fall back to the standard set rather than leaving the workspace with nothing to score against.
  const departments = merged.departments.length > 0 ? merged.departments : STANDARD_DEPTS;
  const territories = merged.territories; // empty = no territories configured; no fallback
  const deptIds    = departments.map(d => d.id);
  const deptLabels = Object.fromEntries(departments.map(d => [d.id, d.label]));
  const roles = [
    { id: 'manager', label: 'Build Manager', desc: 'Full access: approve, export, import, create collections' },
    ...departments.map(d => ({ id: d.id, label: d.label, desc: d.desc })),
  ];
  return { ...merged, departments, territories, deptIds, deptLabels, roles };
}

export const TenantContext = createContext(null);

export function useTenantConfig() {
  return useContext(TenantContext);
}

function getSubdomain() {
  // Local dev (no real subdomain in the URL) has to pick *some* tenant to show — this only
  // affects what a developer sees on localhost, never any tenant's actual data. Override per
  // session with REACT_APP_TENANT_SUBDOMAIN to work against a different tenant locally.
  const override = process.env.REACT_APP_TENANT_SUBDOMAIN;
  if (override) return override;
  const { hostname } = window.location;
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return 'lounge';
  const parts = hostname.split('.');
  return parts.length >= 3 ? parts[0] : null;
}

// Used once in App.jsx to resolve and provide the tenant.
// Components should call useTenantConfig() directly, not this hook.
export function useTenant() {
  const [tenantValue, setTenantValue]   = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError]   = useState(null); // null | 'not_found' | 'unavailable'

  useEffect(() => {
    const subdomain = getSubdomain();
    if (subdomain === 'admin') {
      setTenantValue({ isAdminPortal: true });
      setTenantLoading(false);
      return;
    }
    if (!subdomain) {
      setTenantError('not_found');
      setTenantLoading(false);
      return;
    }
    // get_tenant_for_login returns the full config once signed in (own tenant only) but only
    // branding/allowedDomains/studioOnly before auth — direct table reads are anon-restricted,
    // this RPC is the deliberately narrow public surface (see 20260812_tenant_public_read_lockdown.sql).
    supabase
      .rpc('get_tenant_for_login', { p_subdomain: subdomain })
      .then(({ data, error }) => {
        if (error) {
          setTenantError('unavailable');
        } else if (!data) {
          setTenantError('not_found');
        } else {
          const config = data.config ?? {};
          applyBranding(config.branding);
          // Lets Settings push a saved config into the live app — without it the rest of the
          // app keeps whatever was loaded with the page until a full reload.
          const applyConfig = (next) => setTenantValue(prev => prev && ({ ...prev, ...buildConfig(next ?? {}) }));
          setTenantValue({
            tenantId:   data.id,
            tenantName: data.name,
            subdomain:  data.subdomain,
            applyConfig,
            ...buildConfig(config),
          });
        }
        setTenantLoading(false);
      })
      .catch(() => {
        setTenantError('unavailable');
        setTenantLoading(false);
      });
  }, []);

  return { tenantValue, tenantLoading, tenantError };
}
